import {
  base64UrlToBytes,
  bytesToBase64Url,
  canonicalJson,
  constantTimeEqual,
  ownedBytes,
  sha256,
  utf8,
} from './encoding';
import {
  decodeRecoverySecret,
  type BackupId,
  type RandomBytes,
  type RecoverySecret,
  webCryptoRandomBytes,
} from './recovery-kit';

export const BACKUP_ENVELOPE_VERSION = 1 as const;
export const BACKUP_ALGORITHM = 'A256GCM' as const;
export const GCM_NONCE_BYTE_LENGTH = 12;
export const GCM_TAG_LENGTH = 128;

export type BackupEnvelopeHeader = {
  algorithm: typeof BACKUP_ALGORITHM;
  backupId: BackupId;
  createdAt: string;
  envelopeVersion: typeof BACKUP_ENVELOPE_VERSION;
  schemaVersion: number;
  snapshotId: string;
};

export type BackupEnvelope = {
  header: BackupEnvelopeHeader;
  aadSha256: string;
  nonce: string;
  ciphertext: string;
  ciphertextSha256: string;
};

export class BackupIntegrityError extends Error {
  constructor() {
    super('Backup integrity verification failed');
    this.name = 'BackupIntegrityError';
  }
}

function associatedData(header: BackupEnvelopeHeader): Uint8Array<ArrayBuffer> {
  return utf8(`HoedokLoop Backup Envelope\u0000${canonicalJson(header)}`);
}

async function importEncryptionKey(secret: RecoverySecret, usage: KeyUsage[]): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'raw',
    decodeRecoverySecret(secret),
    { name: 'AES-GCM', length: 256 },
    false,
    usage,
  );
}

function validateHeader(header: BackupEnvelopeHeader): void {
  if (
    header.algorithm !== BACKUP_ALGORITHM ||
    header.envelopeVersion !== BACKUP_ENVELOPE_VERSION ||
    !Number.isSafeInteger(header.schemaVersion) ||
    header.schemaVersion < 1 ||
    header.snapshotId.length < 1 ||
    header.snapshotId.length > 128 ||
    Number.isNaN(Date.parse(header.createdAt))
  ) {
    throw new BackupIntegrityError();
  }
}

export async function encryptBackup(options: {
  plaintext: Uint8Array;
  encryptionKey: RecoverySecret;
  header: Omit<BackupEnvelopeHeader, 'algorithm' | 'envelopeVersion'>;
  randomBytes?: RandomBytes;
}): Promise<BackupEnvelope> {
  const randomBytes = options.randomBytes ?? webCryptoRandomBytes;
  const nonce = ownedBytes(randomBytes(GCM_NONCE_BYTE_LENGTH));
  if (nonce.byteLength !== GCM_NONCE_BYTE_LENGTH) {
    throw new Error('Random source returned an invalid nonce length');
  }
  const header: BackupEnvelopeHeader = {
    algorithm: BACKUP_ALGORITHM,
    backupId: options.header.backupId,
    createdAt: options.header.createdAt,
    envelopeVersion: BACKUP_ENVELOPE_VERSION,
    schemaVersion: options.header.schemaVersion,
    snapshotId: options.header.snapshotId,
  };
  validateHeader(header);
  const aad = associatedData(header);
  const key = await importEncryptionKey(options.encryptionKey, ['encrypt']);
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: nonce,
        additionalData: aad,
        tagLength: GCM_TAG_LENGTH,
      },
      key,
      ownedBytes(options.plaintext),
    ),
  );
  return {
    header,
    aadSha256: bytesToBase64Url(await sha256(aad)),
    nonce: bytesToBase64Url(nonce),
    ciphertext: bytesToBase64Url(ciphertext),
    ciphertextSha256: bytesToBase64Url(await sha256(ciphertext)),
  };
}

export async function decryptBackup(
  envelope: BackupEnvelope,
  encryptionKey: RecoverySecret,
): Promise<Uint8Array<ArrayBuffer>> {
  try {
    validateHeader(envelope.header);
    const nonce = base64UrlToBytes(envelope.nonce);
    const ciphertext = base64UrlToBytes(envelope.ciphertext);
    const aad = associatedData(envelope.header);
    if (nonce.byteLength !== GCM_NONCE_BYTE_LENGTH || ciphertext.byteLength < 16) {
      throw new BackupIntegrityError();
    }
    if (
      !constantTimeEqual(base64UrlToBytes(envelope.aadSha256), await sha256(aad)) ||
      !constantTimeEqual(base64UrlToBytes(envelope.ciphertextSha256), await sha256(ciphertext))
    ) {
      throw new BackupIntegrityError();
    }
    const key = await importEncryptionKey(encryptionKey, ['decrypt']);
    return new Uint8Array(
      await crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: nonce,
          additionalData: aad,
          tagLength: GCM_TAG_LENGTH,
        },
        key,
        ciphertext,
      ),
    );
  } catch (error) {
    if (error instanceof BackupIntegrityError) throw error;
    throw new BackupIntegrityError();
  }
}

export async function rotateBackupEncryption(options: {
  envelope: BackupEnvelope;
  previousKey: RecoverySecret;
  nextKey: RecoverySecret;
  randomBytes?: RandomBytes;
}): Promise<BackupEnvelope> {
  const plaintext = await decryptBackup(options.envelope, options.previousKey);
  return encryptBackup({
    plaintext,
    encryptionKey: options.nextKey,
    header: {
      backupId: options.envelope.header.backupId,
      createdAt: options.envelope.header.createdAt,
      schemaVersion: options.envelope.header.schemaVersion,
      snapshotId: options.envelope.header.snapshotId,
    },
    ...(options.randomBytes === undefined ? {} : { randomBytes: options.randomBytes }),
  });
}
